"use client";

import React from "react";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import Image from "next/image";
import { SwiperSlide } from "swiper/react";
import { Swiper } from "swiper/react";
import { useInView } from "react-intersection-observer";

import ContentLayout from "../layouts/ContentLayout";
import AppText from "../other/AppText";
import { AppIconButton } from "../buttons/AppIconButton";
import { cn } from "@/lib/utils";
import CustomLink from "../other/CustomLink";
import { useSwiper } from "@/hooks/useSwiper";
import AppButton from "../buttons/AppButton";

interface ICategory {
  label: string;
  link: string;
  image: string;
}

interface ICustomizationHeader {
  id: string;
  ctaLabel: string;
  ctaLink: string;
  label: string;
  heading: string;
  subheading: string;
  currentSlug: string;
  categories: ICategory[];
  primaryCtaLabel: string;
  primaryCtaLink: string;
}

export const CustomizationHeader = ({
  id,
  label,
  heading,
  subheading,
  ctaLabel,
  categories,
  currentSlug,
  ctaLink,
  primaryCtaLabel,
  primaryCtaLink,
}: ICustomizationHeader) => {
  const { swiperRef, onSwiperChange, onNextPageClick, onPrevPageClick } = useSwiper();

  const [swiperFirstItemRef, isFirstItemRefFullyInView] = useInView({
    threshold: 1,
  });
  const [swiperLastItemRef, isLastItemRefFullyInView] = useInView({
    threshold: 1,
  });

  return (
    <>
      <ContentLayout bgColorClass="bg-navy-blue" innerClass="sm:pb-0 pb-0 " id={id}>
        <div className="flex flex-col mb-20">
          {label && (
            <AppText type="LABEL_LARGE" className="text-mellow mb-2">
              {label}
            </AppText>
          )}

          <div className="lg:flex-row flex flex-col justify-between gap-8">
            <div className="flex flex-col max-w-[736px]">
              <AppText type="HEADLINE_LARGE" className="text-white mb-4 max-w-[736px]">
                {heading}
              </AppText>
              {subheading && (
                <AppText type="SUB_HEADING_MEDIUM" className=" text-white">
                  {subheading}
                </AppText>
              )}
              {primaryCtaLabel && primaryCtaLink && (
                <div className="sm:justify-center flex justify-start w-full mt-4">
                  <AppButton label={primaryCtaLabel} link={primaryCtaLink} className="sm:w-48 w-full" bg="dark" />
                </div>
              )}
            </div>

            <div>
              <AppIconButton
                intent="secondary"
                label={ctaLabel}
                icon={<Download size={16} aria-label="download icon" />}
                iconOnRightSide
                bg="dark"
                link={ctaLink}
              />
            </div>
          </div>
        </div>
      </ContentLayout>

      <ContentLayout
        className="relative"
        innerClass="sm:pt-0 pt-0 overflow-visible max-w-screen"
        bgColorClass="bg-navy-blue"
      >
        <div className={cn("relative bg-navy-blue lg:w-overflow-right")}>
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onInit={onSwiperChange}
            onSlideChange={onSwiperChange}
            onBreakpoint={onSwiperChange}
            slidesPerView="auto"
            spaceBetween={0}
          >
            {(categories || []).map((category: ICategory, idx: number) => (
              <SwiperSlide key={category.link} className="!w-auto ml-0 p-1">
                <CustomLink
                  href={`/customizations/${category.link}`}
                  className={cn(
                    "flex flex-col border-transparent border-[1px] focus-visible:outline-mellow rounded-[6px] items-center gap-4 p-2 h-full w-[112px]",
                    currentSlug === category.link && "border-mellow bg-[#fcfcfc1a]"
                  )}
                >
                  <Image
                    ref={idx === 0 ? swiperFirstItemRef : idx === categories.length - 1 ? swiperLastItemRef : null}
                    src={category.image}
                    alt={`${category.label} icon`}
                    sizes="100vw"
                    width={0}
                    height={0}
                    className="w-[76px] h-[76px] object-cover rounded-full"
                  />
                  <AppText
                    type="BUTTON_MEDIUM"
                    className={cn("text-center", currentSlug === category.link ? "text-mellow" : "text-white")}
                  >
                    {category.label}
                  </AppText>
                </CustomLink>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div
          className={cn(
            `customizationHeaderArrowsLeft pl-4 pt-10 absolute top-0 w-[137px] 
          left-0 bottom-0 bg-gradient-to-r from-bg-navy-blue to-transparent z-10 transition`,
            isFirstItemRefFullyInView ? "hidden" : "flex"
          )}
        >
          <AppIconButton
            onClick={onPrevPageClick}
            bg={"dark"}
            icon={<ArrowLeft size={20} aria-label="arrow left icon" />}
            intent={"secondary"}
            title={"previous"}
            className="bg-navy-blue h-fit px-3"
          />
        </div>
        <div
          className={cn(
            `customizationHeaderArrowsRight justify-end pr-4 pt-10 absolute top-0 w-[137px] 
          right-0 bottom-0 bg-gradient-to-r from-bg-navy-blue to-transparent z-10 transition`,
            isLastItemRefFullyInView ? "hidden" : "flex"
          )}
        >
          <AppIconButton
            onClick={onNextPageClick}
            bg={"dark"}
            icon={<ArrowRight size={20} aria-label="arrow right icon" />}
            intent={"secondary"}
            title={"next"}
            className="bg-navy-blue h-fit px-3"
          />
        </div>
      </ContentLayout>
    </>
  );
};
